import React, { useEffect, useState } from "react";
import { addNewInvoice } from "../../store/superAdmin/invoicesSlice";
import { uploadInvoiceFiles } from "../../components/utilities/invoiceUpload";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { useToast } from "../../components/utilities/Toast";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSoftInput,
  AdminSoftSelect,
  AdminPrimaryButton,
} from "../../components/admin/AdminPageShell";

type StatusType = "Due" | "Paid" | "Overdue";

const fieldLabel = "block text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-1.5";

const GenerateInvoices: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { boxAccessToken } = useAppSelector((state) => state.auth);

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initialFormData = {
    labToken: "",
    invoiceNumber: "",
    billingPeriod: "",
    amount: "",
    workSummary: [] as string[], // Store multiple file IDs
    paymentLink: "",
    status: "Due" as StatusType,
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!boxAccessToken) dispatch(fetchBoxAccessToken());
  }, [dispatch, boxAccessToken]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement; // type-cast to HTMLInputElement
    const { name, value, files } = target;

    if (name === "workSummary" && files) {
      handleFileUpload(Array.from(files));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Upload multiple files → store Box file IDs
  const handleFileUpload = async (files: File[]) => {
    if (!formData.labToken) {
      alert("Enter Lab Token first!");
      return;
    }

    if (!boxAccessToken) {
      alert("Box Access Token missing!");
      return;
    }

    try {
      setUploading(true);

      const result = await uploadInvoiceFiles(
        formData.labToken,
        boxAccessToken,
        files
      );

      const uploadedFileIds = result.uploadedFileIds || [];

      setFormData((prev) => ({
        ...prev,
        workSummary: [...prev.workSummary, ...uploadedFileIds],
      }));

      showToast("Files uploaded successfully!", "success");
    } catch (error) {
      console.error("Upload failed:", error);
      alert("File upload failed! Try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.workSummary.length === 0) {
      alert("Please upload at least one work summary file!");
      return;
    }

    setSubmitting(true);

    const payload = {
      lab: formData.labToken,
      invoice_Number: formData.invoiceNumber,
      billing_period: formData.billingPeriod,
      amount_due: formData.amount,
      work_Summary: formData.workSummary.join(","), // Combine IDs as CSV
      status: formData.status,
      invoice_link: formData.paymentLink,
    };

    try {
      await dispatch(addNewInvoice(payload));
      showToast("Invoice generated successfully!", "success");
      setFormData(initialFormData);
    } catch (err) {
      console.error(err);
      showToast("Invoice submission failed!", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Generate Invoices</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Generate Invoice"
          subtitle="Create a new lab invoice and attach work summary files"
        />

        <div className="bg-white rounded-2xl border border-[#E8E8E4] shadow-sm p-5 sm:p-6 max-w-3xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* LAB TOKEN + INVOICE NUMBER */}
            <div className="flex flex-col sm:flex-row w-full gap-5">
              <div className="w-full">
                <label className={fieldLabel}>Lab Token</label>
                <AdminSoftInput
                  type="text"
                  name="labToken"
                  value={formData.labToken}
                  onChange={handleChange}
                  placeholder="Enter lab token"
                  className="w-full"
                  required
                />
              </div>

              <div className="w-full">
                <label className={fieldLabel}>Invoice Number</label>
                <AdminSoftInput
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  placeholder="Enter invoice number"
                  className="w-full"
                  required
                />
              </div>
            </div>

            {/* BILLING PERIOD + AMOUNT */}
            <div className="flex flex-col sm:flex-row w-full gap-5">
              <div className="w-full">
                <label className={fieldLabel}>Billing Period</label>
                <AdminSoftInput
                  type="text"
                  name="billingPeriod"
                  value={formData.billingPeriod}
                  onChange={handleChange}
                  className="w-full"
                  required
                />
              </div>

              <div className="w-full">
                <label className={fieldLabel}>Amount</label>
                <AdminSoftInput
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  className="w-full"
                  required
                />
              </div>
            </div>

            {/* FILE UPLOAD + STATUS */}
            <div className="flex flex-col sm:flex-row w-full gap-5">
              <div className="w-full">
                <label className={fieldLabel}>
                  Work Summary (PDF/Image)
                </label>
                <input
                  type="file"
                  name="workSummary"
                  accept=".pdf, image/*"
                  multiple
                  onChange={handleChange}
                  className="w-full text-sm text-[#4A4A48] file:mr-3 file:rounded-lg file:border-0 file:bg-[#F0F7F8] file:px-3 file:py-2 file:text-xs file:font-medium file:text-[#2fa9bf] hover:file:bg-[#E5F4F7]"
                  disabled={!formData.labToken}
                  required
                />

                {uploading && (
                  <p className="text-[#2fa9bf] mt-2 text-sm">Uploading…</p>
                )}

                {formData.workSummary.length > 0 && !uploading && (
                  <p className="mt-2 text-emerald-600 text-sm">
                    Uploaded File IDs: {formData.workSummary.join(", ")}
                  </p>
                )}
              </div>

              <div className="w-full">
                <label className={fieldLabel}>Status</label>
                <AdminSoftSelect
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full"
                >
                  <option value="Due">Due</option>
                  <option value="Paid">Paid</option>
                  <option value="Overdue">Overdue</option>
                </AdminSoftSelect>
              </div>
            </div>

            {/* PAYMENT LINK */}
            <div>
              <label className={fieldLabel}>Payment Link</label>
              <AdminSoftInput
                type="text"
                name="paymentLink"
                value={formData.paymentLink}
                onChange={handleChange}
                placeholder="Enter payment link"
                className="w-full"
              />
            </div>

            {/* SUBMIT */}
            <AdminPrimaryButton
              type="submit"
              disabled={uploading || submitting}
              className="w-full py-3"
            >
              {submitting ? "Submitting…" : "Submit Invoice"}
            </AdminPrimaryButton>
          </form>
        </div>
      </AdminPageShell>
    </>
  );
};

export default GenerateInvoices;
